<template>
    <div class="position-relative">
        <div ref="previewWrapper" class="preview-wrapper" :id="`pfc-picture`"
             :style="{height: `calc(100vh - ${imageHeightDiff}px)`}">
            <json-picture :settings="activeImage.picture" :color="activeImage.color"/>
            <ImageBoomer v-show="!['productType', 'material'].includes(activeImage.type)" :selector="`#pfc-picture`"
                         :src="activeImage.picture.default.path" :alt="activeImage.picture.default.alt"/>
            <div
                v-if="activeImage.generation_type"
                class="image-generation-type"
                :style="imageGenerationTypeStyle"
            >
                {{ $t(`generation_type-${activeImage.generation_type}`) }}
            </div>
        </div>
        <div class="responsive-preview">
            <slick-carousel
                v-if="images.length > 0"
                :slider-settings="{
                    groupsToShow: 1,
                    focusOnSelect: true,
                    infinite: false,
                    adaptiveHeight: true,
                    centerPadding: '0',
                    initialGroupIndex: Math.max(0, images.findIndex(i => i.primary)),
                    variableWidth: false,
                    speed: 300,
                    responsive: [],
                }"
                @after-change="activeIndex = $event"
            >
                <template v-slot="{ swiping }">
                    <div class="pfc-detail-resp-preview"
                         v-for="(image, index) in images"
                         :key="index"
                    >
                        <json-picture :settings="image.picture" :color="image.color" :lazy-load="false"/>
                        <div
                            v-if="activeImage.generation_type"
                            class="image-generation-type"
                        >
                            {{ $t(`generation_type-${activeImage.generation_type}`) }}
                        </div>
                    </div>
                </template>
            </slick-carousel>
        </div>

        <div v-if="images.length > 1" class="thumbs-wrapper">
            <slick-carousel
                v-if="images.length > slidesToShow"
                ref="thumbsCarousel"
                :slider-settings="{
                    groupsToShow: slidesToShow,
                    infinite: true,
                    infiniteLoopOnEdge: true,
                    arrows: true,
                    centerMode: false,
                    centerPadding: '0',
                    initialGroupIndex: Math.max(0, images.findIndex(i => i.primary)),
                    variableWidth: false,
                    speed: 300,
                    responsive: [],
                }"
                @before-change="onThumbsBeforeChange"
            >
                <template v-slot="{ swiping }">
                    <div class="pfc-detail-thumb"
                         v-for="(image, index) in images"
                         :key="index"
                         @click="!swiping && thumbsCarousel?.goTo(index)"
                    >
                        <json-picture :settings="filterPictureThumbnail(image.picture)" :color="image.color"/>
                    </div>
                </template>
            </slick-carousel>
            <!-- Fallback for when there are more than 1 but less than slidesToShow images, v-slick-carousel doesn't work properly with less slides than groupsToShow -->
            <div v-else class="carousel fallback">
                <div class="v-slick-slider">
                    <button
                        type="button"
                        class="v-slick-arrow prev"
                        @click="onThumbsBeforeChange(activeIndex, activeIndex < 1 ? images.length - 1 : activeIndex - 1)"
                    >Previous</button>
                    <div class="v-slick-list">
                        <div class="v-slick-track">
                            <div
                                v-for="(image, index) in images"
                                :key="index"
                                class="v-slick-slide-group active"
                                :class="{ current: index === activeIndex }"
                            >
                                <div class="pfc-detail-thumb" @click="onThumbsBeforeChange(activeIndex, index)">
                                    <json-picture :settings="filterPictureThumbnail(image.picture)" :color="image.color"/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        class="v-slick-arrow next"
                        @click="onThumbsBeforeChange(activeIndex, (activeIndex + 1) % images.length)"
                    >Next</button>
                </div>
            </div>
            <span
                v-if="showAr"
                class="pfc-show-ar ar hide-on-resp"
                v-html="t('product-detail-live-ar')"
                :data-config="arConfig"
                :data-configurator-url="configuratorUrl"
                :data-created-type="arMailCreatedType"
            ></span>
        </div>
    </div>
</template>
<script setup lang="ts">
import {ref, computed, watch, onMounted, onBeforeUnmount, nextTick, useTemplateRef} from 'vue';
import {useI18n} from 'vue-i18n';
import {SlickCarousel} from '@webstacz/shop-components';
import JsonPicture from '@/components/JsonPicture.vue';
import ImageBoomer from '@/components/ImageBoomer.vue';

const THUMBNAIL_BREAKPOINT = 200;
const DEFAULT_IMAGE_PER_SLIDE = 6;
const IMAGE_GENERATION_TYPE_OFFSET = 8;

interface Image {
    picture: any;
    color?: string;
    type: string;
    primary?: boolean;
    generation_type: string;
}

interface Props {
    title: string;
    images: Image[];
    slidesToShow?: number | null;
    showAr: boolean;
    arConfig?: string;
    configuratorUrl?: string;
    arMailCreatedType?: string;
}

const {
    title,
    images,
    slidesToShow: slidesToShowProp = null,
    showAr,
    arConfig = '',
    configuratorUrl = '',
    arMailCreatedType = '',
} = defineProps<Props>();

const {t} = useI18n();

const activeIndex = ref(Math.max(0, images.findIndex(image => image.primary)));
const activeImage = computed(() => images[activeIndex.value]);
const slidesToShow = ref(slidesToShowProp !== null ? parseInt(String(slidesToShowProp)) : DEFAULT_IMAGE_PER_SLIDE);
const imageHeightDiff = ref(250);
const isResponsive = ref(window.innerWidth < 1280);
const imageGenerationTypeStyle = ref<Record<string, string>>({});

const thumbsCarousel = useTemplateRef<InstanceType<typeof SlickCarousel>>('thumbsCarousel');
const previewWrapperEl = useTemplateRef<HTMLDivElement>('previewWrapper');
let previewImageEl: HTMLImageElement | null = null;

const computeImageGenerationTypePosition = () => {
    if (isResponsive.value || !previewWrapperEl.value || !previewImageEl) {
        imageGenerationTypeStyle.value = {};
        return;
    }

    const imageWidth = previewImageEl.naturalWidth;
    const imageHeight = previewImageEl.naturalHeight;
    const imageStyles = window.getComputedStyle(previewImageEl);
    const paddingLeft = parseFloat(imageStyles.paddingLeft) || 0;
    const paddingRight = parseFloat(imageStyles.paddingRight) || 0;
    const paddingTop = parseFloat(imageStyles.paddingTop) || 0;
    const paddingBottom = parseFloat(imageStyles.paddingBottom) || 0;
    const imageContentWidth = previewImageEl.clientWidth - paddingLeft - paddingRight;
    const imageContentHeight = previewImageEl.clientHeight - paddingTop - paddingBottom;

    if (imageWidth === 0 || imageHeight === 0 || imageContentWidth <= 0 || imageContentHeight <= 0) {
        return;
    }

    const imageScale = Math.min(
        1,
        imageContentWidth / imageWidth,
        imageContentHeight / imageHeight,
    );
    const renderedImageWidth = imageWidth * imageScale;
    const renderedImageHeight = imageHeight * imageScale;
    const wrapperBoundingRect = previewWrapperEl.value.getBoundingClientRect();
    const imageBoundingRect = previewImageEl.getBoundingClientRect();
    const imageLeft = imageBoundingRect.left - wrapperBoundingRect.left
        - previewWrapperEl.value.clientLeft
        + previewImageEl.clientLeft
        + paddingLeft
        + (imageContentWidth - renderedImageWidth) / 2;
    const imageTop = imageBoundingRect.top - wrapperBoundingRect.top
        - previewWrapperEl.value.clientTop
        + previewImageEl.clientTop
        + paddingTop
        + (imageContentHeight - renderedImageHeight) / 2;

    imageGenerationTypeStyle.value = {
        right: `${Math.max(0, previewWrapperEl.value.clientWidth - imageLeft - renderedImageWidth)
            + IMAGE_GENERATION_TYPE_OFFSET}px`,
        bottom: `${Math.max(0, previewWrapperEl.value.clientHeight - imageTop - renderedImageHeight)
            + IMAGE_GENERATION_TYPE_OFFSET}px`,
    };
};

const setupPreviewImage = (computePosition = true) => {
    const imageEl = previewWrapperEl.value?.querySelector<HTMLImageElement>('img') ?? null;

    if (previewImageEl === imageEl) {
        if (computePosition) {
            computeImageGenerationTypePosition();
        }
        return;
    }

    previewImageEl?.removeEventListener('load', computeImageGenerationTypePosition);
    previewImageEl = imageEl;
    previewImageEl?.addEventListener('load', computeImageGenerationTypePosition);

    if (computePosition || previewImageEl?.complete) {
        computeImageGenerationTypePosition();
    }
};

const onThumbsBeforeChange = (_oldIndex: number, newIndex: number) => {
    if (newIndex >= 0 && newIndex < images.length) {
        activeIndex.value = newIndex;
    }
};

const filterPictureThumbnail = (picture: any) => {
    const sourcesCopy = !picture.sources ? [] : JSON.parse(JSON.stringify(picture.sources));
    const sources = sourcesCopy.filter((img: any) => img.width && img.width <= THUMBNAIL_BREAKPOINT);

    if (sources.length === 0 && sourcesCopy.length > 0) {
        sources.push(sourcesCopy.sort((a: any, b: any) => a.width - b.width)[0]);
    }

    for (const source of sources) {
        source.media = '';
    }

    return {
        default: picture.default,
        sources,
    };
};

const onWindowResize = () => {
    isResponsive.value = window.innerWidth < 1280;

    let diff = 0;

    const header = document.querySelector('header');
    if (header) {
        diff += header.clientHeight + 60; // 60px for the container padding
    }

    const thumbsWrapper = thumbsCarousel.value?.$el;
    if (thumbsWrapper) {
        diff += thumbsWrapper.clientHeight;
    }

    imageHeightDiff.value = diff + 18;
    nextTick(computeImageGenerationTypePosition);
};

onMounted(() => {
    onWindowResize();
    nextTick(setupPreviewImage);
    window.addEventListener('resize', onWindowResize);
    document.addEventListener('headerBarMove', onWindowResize);
    document.addEventListener('headerBarClose', onWindowResize);
});

onBeforeUnmount(() => {
    previewImageEl?.removeEventListener('load', computeImageGenerationTypePosition);
    window.removeEventListener('resize', onWindowResize);
    document.removeEventListener('headerBarMove', onWindowResize);
    document.removeEventListener('headerBarClose', onWindowResize);
});

watch(activeIndex, () => nextTick(() => setupPreviewImage(false)));
</script>

<style lang="scss" scoped>
:deep(.image-boomer) {
    position: absolute;
    right: 0;
    top: 50px;
    height: 520px;
    width: 640px;
    margin-right: -380px;
}

.carousel.fallback {
    display: flex;

    @media (max-width: 1279px) {
        display: none;
    }

    .v-slick-slider {
        width: 100%;

        .v-slick-list {
            width: 100%;

            .v-slick-track {
                display: flex !important;
                width: auto !important;

                .v-slick-slide-group {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: auto !important;
                }
            }
        }
    }
}

.image-generation-type {
    position: absolute;
    right: 0;
    bottom: 35px;
    border-radius: 9999em;
    font-family: Source Code Pro, monospace;
    font-size: 11px;
    background: #f0f1f2;
    padding: 7px 13px 5px;
    box-shadow: #00000059 0 3px 15px;

    @media (max-width: 1279px) {
        right: 9px;
        bottom: 10px;
        font-size: 10px;
        box-shadow: #00000059 0 2px 9px;
    }
}

.pfc-detail-resp-preview {
    position: relative
}
</style>
